import { useCallback, useEffect, useMemo, useState } from "react";

/**
 * Minimaler async-Resource-Hook ohne TanStack Query.
 *
 * Verbraucht eine async-Funktion + ein Dependency-Array, gibt
 * { status, data, error, refetch } zurueck. Reicht fuers H6-MVP.
 * Wenn Caching/Background-Refetch noetig wird → TanStack Query nachruesten.
 */

export type ApiState<T> =
  | { status: "loading"; data: undefined; error: undefined }
  | { status: "ok"; data: T; error: undefined }
  | { status: "error"; data: undefined; error: Error };

export interface ApiResource<T> {
  state: ApiState<T>;
  refetch: () => void;
}

export function useApi<T>(
  fetcher: () => Promise<T>,
  deps: ReadonlyArray<unknown>
): ApiResource<T> {
  const [state, setState] = useState<ApiState<T>>({
    status: "loading",
    data: undefined,
    error: undefined,
  });
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setState({ status: "loading", data: undefined, error: undefined });
    fetcher()
      .then((data) => {
        if (!cancelled) setState({ status: "ok", data, error: undefined });
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setState({
            status: "error",
            data: undefined,
            error: err instanceof Error ? err : new Error(String(err)),
          });
        }
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, tick]);

  const refetch = useCallback(() => setTick((t) => t + 1), []);
  // Memo der Resource selbst, damit Konsumenten useCallback-Deps stabil
  // halten koennen (sonst ist das Literal `{ state, refetch }` jeden Render
  // referentiell neu, auch wenn state + refetch sich nicht aendern).
  return useMemo(() => ({ state, refetch }), [state, refetch]);
}
